import { useState, useContext, useEffect, useMemo, useRef } from "react";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Divider from "@mui/material/Divider";
import ListItemText from "@mui/material/ListItemText";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

// My Files
import "../../calendar/Calendar.css";
import { DatabaseContext, UserContext } from "../../../context";
import UserAvatar from "../../management/UserAvatar";
import VirtualizedList from "../../VirtualizedList";
import GroupMembersDialog from "../../GroupMembersDialog";
import UnsavedChangesNotice from "../../UnsavedChangesNotice";
import SuccessDialog from "../../SuccessDialog";
import firebase from "../../../firebase";
import { canManageFile } from "../../../utility";

function ResponsiveSearchField({
    onValueChange,
    resetKey,
    value,
    ...textFieldProps
}) {
    const [draftValue, setDraftValue] = useState(value);
    const updateTimerRef = useRef(null);

    useEffect(() => {
        if (updateTimerRef.current !== null) {
            window.clearTimeout(updateTimerRef.current);
            updateTimerRef.current = null;
        }
        setDraftValue(value);
    }, [resetKey, value]);

    useEffect(
        () => () => {
            if (updateTimerRef.current !== null) {
                window.clearTimeout(updateTimerRef.current);
            }
        },
        [],
    );

    const handleChange = (event) => {
        const nextValue = event.target.value;
        setDraftValue(nextValue);

        if (updateTimerRef.current !== null) {
            window.clearTimeout(updateTimerRef.current);
        }
        updateTimerRef.current = window.setTimeout(() => {
            updateTimerRef.current = null;
            onValueChange(nextValue);
        }, 100);
    };

    return (
        <TextField
            {...textFieldProps}
            value={draftValue}
            onChange={handleChange}
        />
    );
}

// Order-independent fingerprint of a selection, for comparing against the
// selection the dialog opened with
const selectionSignature = (items) =>
    (items || [])
        .map((item) => item.key)
        .sort()
        .join(",");

const keySelectionSignature = (keys) =>
    Array.from(keys || [])
        .sort()
        .join(",");

// Virtualised rows have to be a known height, so user rows are pinned to a
// fixed one (their divider is drawn as a border inside it) and section headers
// to another. Both leave a little room on top of what the equivalent MUI rows
// measured before: two lines of text plus the row's own padding and border.
const USER_ROW_HEIGHT = 62;
const SECTION_HEADER_HEIGHT = 36;

const rowTextProps = {
    primaryTypographyProps: {
        variant: "body2",
        fontWeight: 800,
        noWrap: true,
    },
    secondaryTypographyProps: {
        variant: "caption",
        noWrap: true,
    },
};

const plural = (count, noun) => `${count} ${noun}${count === 1 ? "" : "s"}`;

function buildAccessDetails({
    groupsAdded,
    groupsRemoved,
    usersAdded,
    usersRemoved,
}) {
    const details = [];

    if (groupsAdded > 0) {
        details.push(`${plural(groupsAdded, "group")} given access`);
    }
    if (groupsRemoved > 0) {
        details.push(`${plural(groupsRemoved, "group")} removed`);
    }
    if (usersAdded > 0) {
        details.push(`${plural(usersAdded, "user")} given access individually`);
    }
    if (usersRemoved > 0) {
        details.push(`Individual access removed for ${plural(usersRemoved, "user")}`);
    }

    return details;
}

export default function FilePermissionsDialog(props) {
    const { users } = useContext(DatabaseContext);
    const { currentUserData } = useContext(UserContext);

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [managers, setManagers] = useState([]);

    const [usernameFilter, setUsernameFilter] = useState("");
    const [selectedUserFilter, setSelectedUserFilter] = useState("");
    const [availableGroupFilter, setAvailableGroupFilter] = useState("");
    const [selectedGroupFilter, setSelectedGroupFilter] = useState("");

    const [selectedGroups, setSelectedGroups] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [usersTabLoading, setUsersTabLoading] = useState(false);
    // Selection as it stood when the dialog opened, used to detect unsaved edits
    const [openedWith, setOpenedWith] = useState(null);
    // selectedUsers contains both individually selected users and users pulled
    // in by a selected group. Keep their origin separately so removing a group
    // does not also remove an individual selection that pre-dated it.
    const directlySelectedUserKeysRef = useRef(new Set());
    const [confirmation, setConfirmation] = useState(null);
    // The { group, hub } whose members are being viewed, or null
    const [viewingGroupMembers, setViewingGroupMembers] = useState(null);

    useEffect(() => {
        // if (
        //     props.managers.filter((m) => m.key === currentUserData.key)
        //         .length === 0
        // ) {
        //     if (
        //         props.selectedUsers.filter((u) => u.key === currentUserData.key)
        //             .length === 0
        //     ) {
        //         setSelectedUsers([...props.selectedUsers, currentUserData]);
        //     } else {
        //         setSelectedUsers(props.selectedUsers);
        //     }
        // } else {
        //     setSelectedUsers(props.selectedUsers);
        // }
        setSelectedUsers(props.selectedUsers);
        setManagers(props.managers);

        setSelectedGroups(props.selectedGroups);
        const initiallyGroupedUserKeys = new Set(
            (props.selectedGroups || []).flatMap((group) =>
                (group.users || []).map((user) => user.key),
            ),
        );
        directlySelectedUserKeysRef.current = new Set(
            (props.selectedUsers || [])
                .filter(
                    (user) =>
                        !initiallyGroupedUserKeys.has(user.key) ||
                        (props.file &&
                            (user.files || []).includes(props.file.key)),
                )
                .map((user) => user.key),
        );
        setOpenedWith({
            directlySelectedUsers: keySelectionSignature(
                directlySelectedUserKeysRef.current,
            ),
            groups: selectionSignature(props.selectedGroups),
        });
        setUsernameFilter("");
        setSelectedUserFilter("");
        setAvailableGroupFilter("");
        setSelectedGroupFilter("");
        setActiveTab(0);
        setUsersTabLoading(false);
        setConfirmation(null);
        setViewingGroupMembers(null);

        // eslint-disable-next-line
    }, [props.open]);

    const hasUnsavedChanges =
        openedWith !== null &&
        (keySelectionSignature(directlySelectedUserKeysRef.current) !==
            openedWith.directlySelectedUsers ||
            selectionSignature(selectedGroups) !== openedWith.groups);

    // In the upload flow there is no file yet and the selection is just handed
    // back to the parent, so only an existing file needs the permission check
    const canSaveChanges =
        !props.file || canManageFile(props.file, currentUserData);

    useEffect(() => {
        if (activeTab !== 1 || !usersTabLoading) {
            return undefined;
        }

        const frame = window.requestAnimationFrame(() => {
            setUsersTabLoading(false);
        });

        return () => window.cancelAnimationFrame(frame);
    }, [activeTab, usersTabLoading]);

    const handleUserSelect = (user) => () => {
        directlySelectedUserKeysRef.current.add(user.key);
        // A user a selected group already covers is in the list once. Adding
        // them individually promotes that entry rather than duplicating it.
        setSelectedUsers((currentSelectedUsers) =>
            currentSelectedUsers.some(
                (selectedUser) => selectedUser.key === user.key,
            )
                ? [...currentSelectedUsers]
                : [...currentSelectedUsers, user],
        );
        setUsernameFilter("");
    };

    const handleGroupSelect = (group) => () => {
        // Get the users from this group and remove them from the users which can be selected
        let selectedUsersArray = [...selectedUsers];

        group.users.forEach((user) => {
            const userToSelect = users.find((u) => u.key === user.key);

            if (userToSelect) {
                // Avoid duplicates if the user already has access another way.
                if (
                    selectedUsersArray.filter((u) => u.key === userToSelect.key)
                        .length === 0
                ) {
                    selectedUsersArray.push(userToSelect);
                }
            }
        });
        setSelectedUsers(selectedUsersArray);

        // Now select the group
        let selectedArray = [...selectedGroups];
        selectedArray.push(group);
        setSelectedGroups(selectedArray);
        setAvailableGroupFilter("");
    };

    const handleDeleteUser = (user) => () => {
        directlySelectedUserKeysRef.current.delete(user.key);

        // Dropping the individual grant leaves any group access untouched, so
        // the user moves back to the group section rather than off the list
        setSelectedUsers((currentSelectedUsers) =>
            isUserInSelectedGroups(user)
                ? [...currentSelectedUsers]
                : currentSelectedUsers.filter(
                      (selectedUser) => selectedUser.key !== user.key,
                  ),
        );
    };

    const handleViewGroupMembers = (group, hub) => () => {
        setViewingGroupMembers({ group, hub });
    };

    const handleGroupMembersClose = () => {
        setViewingGroupMembers(null);
    };

    const handleDeleteGroup = (group) => () => {
        const remainingGroups = selectedGroups.filter(
            (selectedGroup) => selectedGroup.key !== group.key,
        );
        const removedGroupUserKeys = new Set(
            (group.users || []).map((user) => user.key),
        );
        const remainingGroupUserKeys = new Set(
            remainingGroups.flatMap((selectedGroup) =>
                (selectedGroup.users || []).map((user) => user.key),
            ),
        );

        setSelectedUsers((currentSelectedUsers) =>
            currentSelectedUsers.filter(
                (user) =>
                    !removedGroupUserKeys.has(user.key) ||
                    directlySelectedUserKeysRef.current.has(user.key) ||
                    remainingGroupUserKeys.has(user.key),
            ),
        );
        setSelectedGroups(remainingGroups);
    };

    // Leaves without writing anything, handing the parent back the selection the
    // dialog opened with so the abandoned edits aren't adopted either
    const handleDiscard = () => {
        setUsernameFilter("");
        props.onClose(props.selectedUsers, props.selectedGroups);
    };

    // Clicking the backdrop is easy to do by accident, so it only closes the
    // dialog when there is nothing waiting to be saved
    const handleDialogClose = (event, reason) => {
        if (reason === "backdropClick" && hasUnsavedChanges) {
            return;
        }

        handleDiscard();
    };

    const handleSave = () => {
        setUsernameFilter("");

        // props.file is only set if editing an existing file (not when uploading a new file). In this case save to firebase on close
        if (props.file && canManageFile(props.file, currentUserData)) {
            const fileKey = props.file.key;

            // Write individual leaves rather than replacing whole nodes, and only
            // where access has actually changed. Rebuilding a node wholesale
            // discards any change made between reading it and writing it back.
            const updates = {};

            const previousGroupKeys = new Set(props.file.groups || []);
            const nextGroupKeys = new Set(
                selectedGroups.map((group) => group.key),
            );

            let groupsAdded = 0;
            let groupsRemoved = 0;
            let usersAdded = 0;
            let usersRemoved = 0;

            previousGroupKeys.forEach((groupKey) => {
                if (!nextGroupKeys.has(groupKey)) {
                    updates[`files/${fileKey}/groups/${groupKey}`] = null;
                    groupsRemoved += 1;
                }
            });

            nextGroupKeys.forEach((groupKey) => {
                if (!previousGroupKeys.has(groupKey)) {
                    updates[`files/${fileKey}/groups/${groupKey}`] = true;
                    groupsAdded += 1;
                }
            });

            // An individual grant is recorded whether or not a group also covers
            // the user, so removing that group later does not quietly drop them
            const individualUserKeys = new Set(
                selectedUsers
                    .filter((user) =>
                        directlySelectedUserKeysRef.current.has(user.key),
                    )
                    .map((user) => user.key),
            );

            users.forEach((user) => {
                const hasFile = (user.files || []).includes(fileKey);
                const shouldHaveFile = individualUserKeys.has(user.key);

                if (shouldHaveFile && !hasFile) {
                    updates[`users/${user.key}/files/${fileKey}`] = true;
                    usersAdded += 1;
                } else if (!shouldHaveFile && hasFile) {
                    updates[`users/${user.key}/files/${fileKey}`] = null;
                    usersRemoved += 1;
                }
            });

            if (Object.keys(updates).length > 0) {
                firebase.database().ref().update(updates);

                // Stay open behind the confirmation, which closes the dialog
                setConfirmation(
                    buildAccessDetails({
                        groupsAdded,
                        groupsRemoved,
                        usersAdded,
                        usersRemoved,
                    }),
                );
                return;
            }
        }

        props.onClose(selectedUsers, selectedGroups);
    };

    const handleConfirmationClose = () => {
        setConfirmation(null);
        props.onClose(selectedUsers, selectedGroups);
    };

    const handleTabChange = (event, value) => {
        if (value === 1 && activeTab !== 1) {
            setUsersTabLoading(true);
        }
        setActiveTab(value);
    };

    const selectedGroupUserKeys = useMemo(
        () =>
            new Set(
                selectedGroups.flatMap((group) =>
                    (group.users || []).map((member) => member.key),
                ),
            ),
        [selectedGroups],
    );

    function isUserInSelectedGroups(user) {
        return selectedGroupUserKeys.has(user.key);
    }

    function getGroupName(group, hub) {
        if (group.name === "All Users" && hub) {
            return `All in '${hub.name}'`;
        }
        return group.displayName || group.name;
    }

    function getGroupMemberLabel(group) {
        const memberCount = group.users?.length || 0;
        return `${memberCount} ${memberCount === 1 ? "user" : "users"}`;
    }

    // Who the current user is allowed to see at all. Rolling every ancestor
    // hub's membership into one set turns what was a scan of each hub's user
    // list, for each user, into a single lookup.
    const hubVisibleUserKeys = useMemo(() => {
        const keys = new Set();
        props.ancestorHubs.forEach((hub) =>
            (hub.users || []).forEach((userKey) => keys.add(userKey)),
        );
        return keys;
    }, [props.ancestorHubs]);

    const managerKeys = useMemo(
        () => new Set(managers.map((manager) => manager.key)),
        [managers],
    );

    // Everyone who could appear in the available list, sorted, ignoring the
    // search box and the current selection. Neither the permission check nor the
    // sort depends on those, so both stay off the per-keystroke path, and names
    // are lower-cased once here rather than once per user per keystroke.
    const selectableUsers = useMemo(
        () =>
            users
                .filter((user) => {
                    if (user.key === currentUserData.key) return false;
                    if (managerKeys.has(user.key)) return false;

                    return (
                        currentUserData.role === "System Digital Manager" ||
                        user.createdBy === currentUserData.key ||
                        (user.admins || []).includes(currentUserData.key) ||
                        hubVisibleUserKeys.has(user.key)
                    );
                })
                .map((user) => ({
                    user,
                    searchName: (user.fullName || "").toLowerCase(),
                    sortName: (user.lastName || "").toLowerCase(),
                }))
                .sort((a, b) => (a.sortName > b.sortName ? 1 : -1)),
        [users, currentUserData, managerKeys, hubVisibleUserKeys],
    );

    const sortedUsers = (usersToSort) =>
        [...usersToSort].sort((a, b) =>
            (a.lastName || "").toLowerCase() > (b.lastName || "").toLowerCase()
                ? 1
                : -1,
        );

    const withSearchNames = (list) =>
        list.map((user) => ({
            user,
            searchName: (user.fullName || "").toLowerCase(),
        }));

    // Individual access is tracked separately from group cover, so a user can
    // hold both: the group brings them in now, the individual grant keeps them
    // if that group is later removed. Those covered only by a group are listed
    // read-only next to the individual selections, so it is clear who a selected
    // group actually brings in. Splitting and sorting once per change of
    // selection keeps that off the per-keystroke path too.
    const { directlySelectedUsers, groupSelectedUsers } = useMemo(() => {
        const directly = [];
        const viaGroups = [];

        selectedUsers.forEach((user) => {
            if (directlySelectedUserKeysRef.current.has(user.key)) {
                directly.push(user);
            } else if (selectedGroupUserKeys.has(user.key)) {
                viaGroups.push(user);
            }
        });

        return {
            directlySelectedUsers: withSearchNames(sortedUsers(directly)),
            groupSelectedUsers: withSearchNames(sortedUsers(viaGroups)),
        };
        // The ref is only ever mutated alongside a selectedUsers update, so that
        // state change is what re-runs this
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUsers, selectedGroupUserKeys]);

    const shouldPrepareUserLists = activeTab === 1 && !usersTabLoading;

    // All a keystroke costs now: one pass over an already sorted list
    const matchingUsers = (entries, filter) => {
        const query = filter.trim().toLowerCase();
        const matches = [];

        entries.forEach((entry) => {
            if (entry.searchName.includes(query)) {
                matches.push(entry.user);
            }
        });

        return matches;
    };

    const availableUsers = shouldPrepareUserLists
        ? matchingUsers(
              // Only an individual grant takes a user out of this list. Someone
              // a selected group covers can still be added in their own right.
              selectableUsers.filter(
                  (entry) =>
                      !directlySelectedUserKeysRef.current.has(entry.user.key),
              ),
              usernameFilter,
          )
        : [];

    const filteredSelectedUsers = shouldPrepareUserLists
        ? matchingUsers(directlySelectedUsers, selectedUserFilter)
        : [];

    const filteredGroupSelectedUsers = shouldPrepareUserLists
        ? matchingUsers(groupSelectedUsers, selectedUserFilter)
        : [];

    const sortedAncestorHubs = [...props.ancestorHubs].sort(
        (a, b) => a.ancestors.length - b.ancestors.length,
    );

    const groupOptions = sortedAncestorHubs.flatMap((hub) =>
        [...hub.groups]
            .sort((a, b) =>
                getGroupName(a, hub).localeCompare(getGroupName(b, hub)),
            )
            .map((group) => ({ group, hub })),
    );

    const hubOrder = new Map(
        sortedAncestorHubs.map((hub, index) => [hub.key, index]),
    );

    const getHubBreadcrumb = (hub) => {
        if (!hub) return "Hub unavailable";

        const ancestorHubNames = sortedAncestorHubs
            .filter(
                (ancestorHub) =>
                    ancestorHub.key !== hub.key &&
                    hub.ancestors?.includes(ancestorHub.key),
            )
            .map((ancestorHub) => ancestorHub.name);

        return [...ancestorHubNames, hub.name].join(" › ");
    };

    const groupMatchesFilter = (option, filter) => {
        const searchText = `${getGroupName(option.group, option.hub)} ${
            option.hub?.name || ""
        }`.toLowerCase();
        return searchText.includes(filter.trim().toLowerCase());
    };

    const selectedGroupOptions = selectedGroups
        .map((group) => ({
            group,
            hub: sortedAncestorHubs.find((hub) =>
                hub.groups.some((hubGroup) => hubGroup.key === group.key),
            ),
        }))
        .sort((a, b) => {
            const hubComparison =
                (hubOrder.get(a.hub?.key) ?? Number.MAX_SAFE_INTEGER) -
                (hubOrder.get(b.hub?.key) ?? Number.MAX_SAFE_INTEGER);
            return (
                hubComparison ||
                getGroupName(a.group, a.hub).localeCompare(
                    getGroupName(b.group, b.hub),
                )
            );
        });

    const filteredSelectedGroupOptions = selectedGroupOptions.filter((option) =>
        groupMatchesFilter(option, selectedGroupFilter),
    );

    // Which selected groups each user is covered by, so the read-only rows in
    // the users tab can say where that access came from
    const groupNamesByUserKey = new Map();
    selectedGroupOptions.forEach(({ group, hub }) => {
        const groupName = getGroupName(group, hub);
        (group.users || []).forEach((member) => {
            const existingNames = groupNamesByUserKey.get(member.key);
            if (existingNames) {
                existingNames.push(groupName);
            } else {
                groupNamesByUserKey.set(member.key, [groupName]);
            }
        });
    });

    const getGroupAccessSummary = (user) => {
        const groupNames = groupNamesByUserKey.get(user.key) || [];

        if (groupNames.length === 0) return "Via a group";
        if (groupNames.length === 1) return `Via ${groupNames[0]}`;
        return `Via ${groupNames[0]} +${groupNames.length - 1} more`;
    };

    const getAlsoInGroupSummary = (user) => {
        const groupNames = groupNamesByUserKey.get(user.key) || [];

        if (groupNames.length === 0) return "Also in a selected group";
        if (groupNames.length === 1) return `Also in ${groupNames[0]}`;
        return `Also in ${groupNames[0]} +${groupNames.length - 1} more`;
    };

    const getAlsoInGroupTitle = (user) => {
        const groupNames = groupNamesByUserKey.get(user.key) || [];
        const groupList =
            groupNames.length === 0 ? "a selected group" : groupNames.join(", ");

        return `${user.fullName} is also covered by ${groupList}. Removing them here leaves that group access in place.`;
    };

    const getGroupAccessTitle = (user) => {
        const groupNames = groupNamesByUserKey.get(user.key) || [];

        if (groupNames.length === 0) {
            return `${user.fullName} has access through a selected group. Remove that group to change this.`;
        }

        return `${user.fullName} has access through ${groupNames.join(
            ", ",
        )}. Remove the ${
            groupNames.length === 1 ? "group" : "groups"
        } to change this.`;
    };

    const unselectedGroupOptions = groupOptions.filter(
        (option) =>
            !selectedGroups.some(
                (selectedGroup) => selectedGroup.key === option.group.key,
            ),
    );

    const availableGroupOptions = unselectedGroupOptions.filter((option) =>
        groupMatchesFilter(option, availableGroupFilter),
    );

    // Group membership is stored as keys only, so pair each one back up with the
    // user it refers to for the member list
    const viewedGroupMembers = viewingGroupMembers
        ? sortedUsers(
              (viewingGroupMembers.group.users || []).map(
                  (member) =>
                      users.find((user) => user.key === member.key) || {
                          key: member.key,
                          fullName: "User unavailable",
                          lastName: "",
                      },
              ),
          )
        : [];

    const renderGroupHubHeader = (hub) => (
        <Box
            sx={{
                position: "sticky",
                top: 0,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.8,
                color: "text.secondary",
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.default",
            }}
        >
            <AccountTreeOutlinedIcon sx={{ fontSize: 17, flexShrink: 0 }} />
            <Typography
                variant="caption"
                title={getHubBreadcrumb(hub)}
                sx={{
                    minWidth: 0,
                    fontWeight: 800,
                    letterSpacing: "0.025em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    direction: "rtl",
                    textAlign: "left",
                    unicodeBidi: "plaintext",
                }}
            >
                {getHubBreadcrumb(hub)}
            </Typography>
        </Box>
    );

    const renderSelectedUserSectionHeader = ({ icon, label, users }) => (
        <Box
            sx={{
                height: "100%",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.8,
                color: "text.secondary",
                borderBottom: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.default",
            }}
        >
            {icon}
            <Typography
                variant="caption"
                sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 800,
                    letterSpacing: "0.025em",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {label}
            </Typography>
            <Typography variant="caption" sx={{ flexShrink: 0 }}>
                {users.length}
            </Typography>
        </Box>
    );

    const renderSelectedUserRow = (user, viaGroup) => {
        // Held individually and covered by a group: the row stays removable,
        // but says the group access will outlive the individual grant
        const alsoInGroup = !viaGroup && isUserInSelectedGroups(user);

        return (
            <Box
                title={
                    viaGroup
                        ? getGroupAccessTitle(user)
                        : alsoInGroup
                          ? getAlsoInGroupTitle(user)
                          : undefined
                }
                sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.25,
                    py: 0.65,
                    opacity: viaGroup ? 0.62 : 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Box sx={{ flexShrink: 0 }}>
                    <UserAvatar
                        userOrHub={user}
                        path="users"
                        size="small"
                        variant="circular"
                    />
                </Box>
                <ListItemText
                    primary={user.fullName}
                    secondary={
                        viaGroup
                            ? getGroupAccessSummary(user)
                            : alsoInGroup
                              ? getAlsoInGroupSummary(user)
                              : user.customRoleName
                    }
                    {...rowTextProps}
                />
                {viaGroup ? (
                    <IconButton
                        aria-label={`${user.fullName} cannot be removed individually`}
                        disabled
                        size="small"
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                ) : (
                    user.key !== currentUserData?.key && (
                        <IconButton
                            aria-label={`Remove ${user.fullName}`}
                            onClick={handleDeleteUser(user)}
                            size="small"
                            sx={{ color: "error.main" }}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    )
                )}
            </Box>
        );
    };

    const renderAvailableUserRow = (user, index) => (
        <ListItemButton
            onClick={handleUserSelect(user)}
            sx={{
                height: "100%",
                gap: 1,
                px: 1.25,
                py: 0.65,
                borderBottom: "1px solid",
                borderColor:
                    index < availableUsers.length - 1
                        ? "divider"
                        : "transparent",
            }}
        >
            <Box sx={{ flexShrink: 0 }}>
                <UserAvatar
                    userOrHub={user}
                    path="users"
                    size="small"
                    variant="circular"
                />
            </Box>
            <ListItemText
                primary={user.fullName}
                secondary={user.customRoleName}
                {...rowTextProps}
            />
            <AddCircleOutlineIcon
                fontSize="small"
                sx={{ color: "primary.main" }}
            />
        </ListItemButton>
    );

    const selectedUserSections = [
        {
            key: "individual",
            label: "Added individually",
            icon: <PersonOutlineIcon sx={{ fontSize: 17, flexShrink: 0 }} />,
            users: filteredSelectedUsers,
            viaGroup: false,
        },
        {
            key: "viaGroups",
            label: "Included by a group",
            icon: <GroupsOutlinedIcon sx={{ fontSize: 17, flexShrink: 0 }} />,
            users: filteredGroupSelectedUsers,
            viaGroup: true,
        },
    ].filter((section) => section.users.length > 0);

    // With only one section on show its header would just repeat the panel title
    const showSelectedUserSectionHeaders = selectedUserSections.length > 1;

    // Headers and rows share one scroll container, so they share one flat list
    // of items for it to window over
    const selectedUserItems = [];
    selectedUserSections.forEach((section) => {
        if (showSelectedUserSectionHeaders) {
            selectedUserItems.push({
                key: `section-${section.key}`,
                header: section,
            });
        }

        section.users.forEach((user) => {
            selectedUserItems.push({
                key: user.key,
                user,
                viaGroup: section.viaGroup,
            });
        });
    });

    return (
        <>
        <Dialog
            onClose={handleDialogClose}
            open={props.open}
            fullWidth
            maxWidth="lg"
            aria-labelledby="file-access-dialog-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        maxHeight: "92vh",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <GroupsOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="file-access-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Manage access
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Choose the groups and individual users who can access
                        this file.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close without saving access settings"
                    onClick={handleDiscard}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(3, minmax(0, 1fr))",
                        },
                        gap: 1,
                        mb: 2,
                    }}
                >
                    {[
                        {
                            icon: <ShieldOutlinedIcon />,
                            label: "Managers",
                            value: managers.length,
                        },
                        {
                            icon: <GroupsOutlinedIcon />,
                            label: "Groups",
                            value: selectedGroups.length,
                            // Counted across groups, so anyone in more than one
                            // selected group is only counted once
                            caption: `Containing ${plural(
                                selectedGroupUserKeys.size,
                                "user",
                            )}`,
                        },
                        {
                            icon: <PersonOutlineIcon />,
                            label: "Individual users",
                            value: directlySelectedUsers.length,
                        },
                    ].map((summary) => (
                        <Box
                            key={summary.label}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.25,
                                p: 1.5,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                backgroundColor: "background.paper",
                            }}
                        >
                            <Box
                                sx={{
                                    color: "primary.main",
                                    display: "grid",
                                }}
                            >
                                {summary.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {summary.label}
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{ lineHeight: 1 }}
                                >
                                    {summary.value}
                                </Typography>
                                {summary.caption && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.4 }}
                                    >
                                        {summary.caption}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Box
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        mb: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: managers.length > 0 ? 1.5 : 0,
                        }}
                    >
                        <ShieldOutlinedIcon
                            sx={{ color: "text.secondary" }}
                        />
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 800 }}
                            >
                                File managers
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Managers always retain access to this file.
                            </Typography>
                        </Box>
                    </Box>
                    {managers.length > 0 && (
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.75,
                            }}
                        >
                            {sortedUsers(managers).map((manager) => (
                                <Chip
                                    key={manager.key}
                                    label={manager.fullName}
                                    size="small"
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                <Box
                    sx={{
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        overflow: "hidden",
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant="fullWidth"
                        sx={{
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Tab
                            icon={<GroupsOutlinedIcon />}
                            iconPosition="start"
                            label={`Groups (${selectedGroups.length})`}
                        />
                        <Tab
                            icon={<PersonOutlineIcon />}
                            iconPosition="start"
                            label={`Users (${directlySelectedUsers.length})`}
                        />
                    </Tabs>

                    {activeTab === 0 && (
                        <Box
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "minmax(0, 1fr)",
                                    md: "repeat(2, minmax(0, 1fr))",
                                },
                                gap: 2,
                                backgroundColor: "background.default",
                            }}
                        >
                            <Box
                                sx={{
                                    minWidth: 0,
                                    p: { xs: 1.5, sm: 2 },
                                    borderRadius: 2.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "background.paper",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "baseline",
                                        justifyContent: "space-between",
                                        mb: 1.25,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Selected groups
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {selectedGroups.length}
                                    </Typography>
                                </Box>
                                <ResponsiveSearchField
                                    id="selected-file-access-group-search"
                                    label="Search selected groups"
                                    size="small"
                                    fullWidth
                                    value={selectedGroupFilter}
                                    onValueChange={setSelectedGroupFilter}
                                    resetKey={props.open}
                                    sx={{
                                        mb: 1.25,
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                        },
                                    }}
                                />
                                {filteredSelectedGroupOptions.length === 0 ? (
                                    <Box
                                        sx={{
                                            height: { md: 390 },
                                            minHeight: 150,
                                            display: "grid",
                                            placeItems: "center",
                                            textAlign: "center",
                                            px: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {selectedGroups.length === 0
                                                ? "No groups currently have access."
                                                : "No selected groups match this search."}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <List
                                        disablePadding
                                        sx={{
                                            height: { md: 390 },
                                            maxHeight: { xs: 300, md: 390 },
                                            overflowY: "auto",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                    >
                                        {filteredSelectedGroupOptions.map(
                                            ({ group, hub }, index) => {
                                                const hubKey =
                                                    hub?.key || "unavailable";
                                                const previousHubKey =
                                                    filteredSelectedGroupOptions[
                                                        index - 1
                                                    ]?.hub?.key ||
                                                    "unavailable";
                                                const nextHubKey =
                                                    filteredSelectedGroupOptions[
                                                        index + 1
                                                    ] === undefined
                                                        ? null
                                                        : filteredSelectedGroupOptions[
                                                              index + 1
                                                          ].hub?.key ||
                                                          "unavailable";

                                                return (
                                                    <Box key={group.key}>
                                                        {(index === 0 ||
                                                            hubKey !==
                                                                previousHubKey) &&
                                                            renderGroupHubHeader(
                                                                hub,
                                                            )}
                                                    <ListItemButton
                                                        // Rendered as a div so
                                                        // the remove button is
                                                        // not nested in a button
                                                        component="div"
                                                        onClick={handleViewGroupMembers(
                                                            group,
                                                            hub,
                                                        )}
                                                        aria-label={`View the members of ${getGroupName(
                                                            group,
                                                            hub,
                                                        )}`}
                                                        sx={{
                                                            gap: 1,
                                                            px: 1.25,
                                                            py: 0.85,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: 1.5,
                                                                display: "grid",
                                                                placeItems:
                                                                    "center",
                                                                flexShrink: 0,
                                                                color: "primary.main",
                                                                backgroundColor:
                                                                    "rgba(0, 149, 61, 0.09)",
                                                            }}
                                                        >
                                                            <GroupsOutlinedIcon fontSize="small" />
                                                        </Box>
                                                        <ListItemText
                                                            primary={getGroupName(
                                                                group,
                                                                hub,
                                                            )}
                                                            secondary={getGroupMemberLabel(
                                                                group,
                                                            )}
                                                            primaryTypographyProps={{
                                                                variant:
                                                                    "body2",
                                                                fontWeight: 800,
                                                                noWrap: true,
                                                            }}
                                                            secondaryTypographyProps={{
                                                                variant:
                                                                    "caption",
                                                                noWrap: true,
                                                            }}
                                                        />
                                                        <IconButton
                                                            aria-label={`Remove ${getGroupName(
                                                                group,
                                                                hub,
                                                            )}`}
                                                            onClick={(
                                                                event,
                                                            ) => {
                                                                event.stopPropagation();
                                                                handleDeleteGroup(
                                                                    group,
                                                                )();
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                color: "error.main",
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </ListItemButton>
                                                        {hubKey ===
                                                            nextHubKey && (
                                                            <Divider />
                                                        )}
                                                    </Box>
                                                );
                                            },
                                        )}
                                    </List>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    minWidth: 0,
                                    p: { xs: 1.5, sm: 2 },
                                    borderRadius: 2.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "background.paper",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "baseline",
                                        justifyContent: "space-between",
                                        mb: 1.25,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Available groups
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {availableGroupOptions.length}
                                    </Typography>
                                </Box>
                                <ResponsiveSearchField
                                    id="available-file-access-group-search"
                                    label="Search available groups"
                                    size="small"
                                    fullWidth
                                    value={availableGroupFilter}
                                    onValueChange={setAvailableGroupFilter}
                                    resetKey={props.open}
                                    sx={{
                                        mb: 1.25,
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                        },
                                    }}
                                />
                                {availableGroupOptions.length === 0 ? (
                                    <Box
                                        sx={{
                                            height: { md: 390 },
                                            minHeight: 150,
                                            display: "grid",
                                            placeItems: "center",
                                            textAlign: "center",
                                            px: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {unselectedGroupOptions.length === 0
                                                ? "All available groups have been selected."
                                                : "No available groups match this search."}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <List
                                        disablePadding
                                        sx={{
                                            height: { md: 390 },
                                            maxHeight: { xs: 300, md: 390 },
                                            overflowY: "auto",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                    >
                                        {availableGroupOptions.map(
                                            ({ group, hub }, index) => {
                                                const hubKey =
                                                    hub?.key || "unavailable";
                                                const previousHubKey =
                                                    availableGroupOptions[
                                                        index - 1
                                                    ]?.hub?.key ||
                                                    "unavailable";
                                                const nextHubKey =
                                                    availableGroupOptions[
                                                        index + 1
                                                    ] === undefined
                                                        ? null
                                                        : availableGroupOptions[
                                                              index + 1
                                                          ].hub?.key ||
                                                          "unavailable";

                                                return (
                                                    <Box key={group.key}>
                                                        {(index === 0 ||
                                                            hubKey !==
                                                                previousHubKey) &&
                                                            renderGroupHubHeader(
                                                                hub,
                                                            )}
                                                    <ListItemButton
                                                        onClick={handleGroupSelect(
                                                            group,
                                                        )}
                                                        sx={{
                                                            gap: 1,
                                                            px: 1.25,
                                                            py: 0.85,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: 36,
                                                                height: 36,
                                                                borderRadius: 1.5,
                                                                display: "grid",
                                                                placeItems:
                                                                    "center",
                                                                flexShrink: 0,
                                                                color: "text.secondary",
                                                                backgroundColor:
                                                                    "action.hover",
                                                            }}
                                                        >
                                                            <GroupsOutlinedIcon fontSize="small" />
                                                        </Box>
                                                        <ListItemText
                                                            primary={getGroupName(
                                                                group,
                                                                hub,
                                                            )}
                                                            secondary={getGroupMemberLabel(
                                                                group,
                                                            )}
                                                            primaryTypographyProps={{
                                                                variant:
                                                                    "body2",
                                                                fontWeight: 800,
                                                                noWrap: true,
                                                            }}
                                                            secondaryTypographyProps={{
                                                                variant:
                                                                    "caption",
                                                                noWrap: true,
                                                            }}
                                                        />
                                                        <AddCircleOutlineIcon
                                                            fontSize="small"
                                                            sx={{
                                                                color: "primary.main",
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                        {hubKey ===
                                                            nextHubKey && (
                                                            <Divider />
                                                        )}
                                                    </Box>
                                                );
                                            },
                                        )}
                                    </List>
                                )}
                            </Box>
                        </Box>
                    )}

                    {activeTab === 1 && (
                        <Box
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "minmax(0, 1fr)",
                                    md: "repeat(2, minmax(0, 1fr))",
                                },
                                gap: 2,
                                backgroundColor: "background.default",
                            }}
                        >
                            <Box
                                sx={{
                                    minWidth: 0,
                                    p: { xs: 1.5, sm: 2 },
                                    borderRadius: 2.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "background.paper",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "baseline",
                                        justifyContent: "space-between",
                                        mb: 1.25,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Selected users
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {usersTabLoading
                                            ? "—"
                                            : groupSelectedUsers.length > 0
                                              ? `${directlySelectedUsers.length} individual · ${groupSelectedUsers.length} via groups`
                                              : directlySelectedUsers.length}
                                    </Typography>
                                </Box>
                                <ResponsiveSearchField
                                    id="selected-file-access-user-search"
                                    label="Search selected users"
                                    size="small"
                                    fullWidth
                                    value={selectedUserFilter}
                                    disabled={usersTabLoading}
                                    onValueChange={setSelectedUserFilter}
                                    resetKey={props.open}
                                    sx={{
                                        mb: 1.25,
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                        },
                                    }}
                                />
                                {usersTabLoading ? (
                                    <Box
                                        sx={{
                                            height: { md: 390 },
                                            minHeight: 150,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 1.25,
                                            textAlign: "center",
                                            px: 2,
                                        }}
                                    >
                                        <CircularProgress size={26} />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Loading selected users…
                                        </Typography>
                                    </Box>
                                ) : selectedUserSections.length === 0 ? (
                                    <Box
                                        sx={{
                                            height: { md: 390 },
                                            minHeight: 150,
                                            display: "grid",
                                            placeItems: "center",
                                            textAlign: "center",
                                            px: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {directlySelectedUsers.length +
                                                groupSelectedUsers.length ===
                                            0
                                                ? "No users have been added individually."
                                                : "No selected users match this search."}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <VirtualizedList
                                        items={selectedUserItems}
                                        itemHeight={(item) =>
                                            item.header
                                                ? SECTION_HEADER_HEIGHT
                                                : USER_ROW_HEIGHT
                                        }
                                        getItemKey={(item) => item.key}
                                        isSticky={(item) =>
                                            Boolean(item.header)
                                        }
                                        renderItem={(item) =>
                                            item.header
                                                ? renderSelectedUserSectionHeader(
                                                      item.header,
                                                  )
                                                : renderSelectedUserRow(
                                                      item.user,
                                                      item.viaGroup,
                                                  )
                                        }
                                        sx={{
                                            height: { md: 390 },
                                            maxHeight: { xs: 300, md: 390 },
                                            overflowY: "auto",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                    />
                                )}
                            </Box>

                            <Box
                                sx={{
                                    minWidth: 0,
                                    p: { xs: 1.5, sm: 2 },
                                    borderRadius: 2.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    backgroundColor: "background.paper",
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "baseline",
                                        justifyContent: "space-between",
                                        mb: 1.25,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 800 }}
                                    >
                                        Available users
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {usersTabLoading
                                            ? "—"
                                            : availableUsers.length}
                                    </Typography>
                                </Box>
                                <ResponsiveSearchField
                                    id="available-file-access-user-search"
                                    label="Search available users"
                                    size="small"
                                    fullWidth
                                    value={usernameFilter}
                                    disabled={usersTabLoading}
                                    onValueChange={setUsernameFilter}
                                    resetKey={props.open}
                                    sx={{
                                        mb: 1.25,
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: 2,
                                        },
                                    }}
                                />
                                {usersTabLoading ? (
                                    <Box
                                        sx={{
                                            height: { md: 390 },
                                            minHeight: 150,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: 1.25,
                                            textAlign: "center",
                                            px: 2,
                                        }}
                                    >
                                        <CircularProgress size={26} />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            Loading available users…
                                        </Typography>
                                    </Box>
                                ) : availableUsers.length === 0 ? (
                                    <Box
                                        sx={{
                                            height: { md: 390 },
                                            minHeight: 150,
                                            display: "grid",
                                            placeItems: "center",
                                            textAlign: "center",
                                            px: 2,
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            No matching users are available.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <VirtualizedList
                                        items={availableUsers}
                                        itemHeight={USER_ROW_HEIGHT}
                                        getItemKey={(user) => user.key}
                                        renderItem={renderAvailableUserRow}
                                        sx={{
                                            height: { md: 390 },
                                            maxHeight: { xs: 300, md: 390 },
                                            overflowY: "auto",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}>
                {hasUnsavedChanges && <UnsavedChangesNotice />}
                <Button color="inherit" onClick={handleDiscard}>
                    {hasUnsavedChanges ? "Discard changes" : "Close"}
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={handleSave}
                    disabled={!hasUnsavedChanges || !canSaveChanges}
                >
                    Save changes
                </Button>
            </DialogActions>
        </Dialog>
        <GroupMembersDialog
            open={viewingGroupMembers !== null}
            groupName={
                viewingGroupMembers
                    ? getGroupName(
                          viewingGroupMembers.group,
                          viewingGroupMembers.hub,
                      )
                    : ""
            }
            breadcrumb={
                viewingGroupMembers
                    ? getHubBreadcrumb(viewingGroupMembers.hub)
                    : ""
            }
            members={viewedGroupMembers}
            onClose={handleGroupMembersClose}
        />
        <SuccessDialog
            open={confirmation !== null}
            onClose={handleConfirmationClose}
            title="Access updated"
            details={confirmation || []}
        />
        </>
    );
}
